import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ProductDto, ProductsApiResponse } from '../../types';
import styles from './Dashboard.module.css';

const SEUIL_CRITIQUE = 5;

interface CommandeApi {
  id: number;
  nomClient: string;
  prenomClient: string;
  total: number;
  statutPaiement: string;
  statutLivraison: string;
  dateCommande: string;
}

export const Dashboard: React.FC = () => {
  const [commandes, setCommandes] = useState<CommandeApi[]>([]);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [cmdRes, prodRes] = await Promise.all([
        api.get<CommandeApi[]>('commandes'),
        api.get<ProductsApiResponse>('products/admin'),
      ]);
      setCommandes(cmdRes.data ?? []);
      setProducts(prodRes.data?.data?.products ?? []);
    } catch (err: any) {
      const status = err.response?.status;
      const detail = status ? ` (HTTP ${status})` : ` (${err.message || 'Network Error'})`;
      setError('Impossible de charger les données' + detail);
    } finally {
      setLoading(false);
    }
  };

  /* ── Stats commandes ── */
  const statsCommandes = useMemo(() => {
    const today = new Date().toDateString();
    const thisMonth = new Date();
    return {
      nbTotal: commandes.length,
      caTotal: commandes.reduce((s, c) => s + (c.total ?? 0), 0),
      caAujourdhui: commandes
        .filter((c) => new Date(c.dateCommande).toDateString() === today)
        .reduce((s, c) => s + (c.total ?? 0), 0),
      caMois: commandes
        .filter((c) => {
          const d = new Date(c.dateCommande);
          return d.getMonth() === thisMonth.getMonth() && d.getFullYear() === thisMonth.getFullYear();
        })
        .reduce((s, c) => s + (c.total ?? 0), 0),
      enAttentePaiement: commandes.filter((c) => c.statutPaiement === 'en_attente').length,
      enPreparation: commandes.filter((c) => c.statutLivraison === 'en_preparation').length,
      livrees: commandes.filter((c) => c.statutLivraison === 'livre').length,
    };
  }, [commandes]);

  /* ── Stats stock ── */
  const statsStock = useMemo(() => {
    const ruptures = products.filter((p) => p.stock === 0 && !p.masquer);
    const critiques = products.filter((p) => p.stock > 0 && p.stock <= SEUIL_CRITIQUE && !p.masquer);
    const valeur = products.reduce((s, p) => s + p.stock * p.price, 0);
    const dispo = products.filter((p) => !p.masquer);
    const tauxDispo = dispo.length > 0
      ? Math.round((dispo.filter((p) => p.stock > 0).length / dispo.length) * 100)
      : 0;
    const masques = products.filter((p) => p.masquer).length;
    return { ruptures, critiques, valeur, tauxDispo, masques, total: products.length };
  }, [products]);

  /* ── Commandes bloquées (en_preparation > 3 jours) ── */
  const commandesBloquees = useMemo(() => {
    const limite = new Date();
    limite.setDate(limite.getDate() - 3);
    return commandes
      .filter(
        (c) =>
          c.statutLivraison === 'en_preparation' &&
          new Date(c.dateCommande) < limite
      )
      .sort((a, b) => new Date(a.dateCommande).getTime() - new Date(b.dateCommande).getTime())
      .slice(0, 6);
  }, [commandes]);

  /* ── Commandes récentes ── */
  const recentCommandes = useMemo(
    () =>
      [...commandes]
        .sort((a, b) => new Date(b.dateCommande).getTime() - new Date(a.dateCommande).getTime())
        .slice(0, 5),
    [commandes]
  );

  const fmt = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const getBadgePaiement = (statut: string) => {
    switch (statut) {
      case 'paye':      return <span className={`${styles.badge} ${styles.badgeGreen}`}>Payé</span>;
      case 'en_attente':return <span className={`${styles.badge} ${styles.badgeOrange}`}>En attente</span>;
      case 'echec':     return <span className={`${styles.badge} ${styles.badgeRed}`}>Échec</span>;
      case 'rembourse': return <span className={`${styles.badge} ${styles.badgeBlue}`}>Remboursé</span>;
      default:          return <span className={styles.badge}>{statut}</span>;
    }
  };

  const moisLabel = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <Layout>
      <div className={styles.container}>

        {/* EN-TÊTE */}
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.title}>Dashboard</h1>
            {user && <p className={styles.welcome}>Bienvenue, {user.FirstName} {user.LastName}</p>}
          </div>
          <button onClick={loadData} className={styles.btnRefresh} disabled={loading}>
            {loading ? 'Chargement...' : '↻ Actualiser'}
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {loading ? (
          <div className={styles.loading}>Chargement des statistiques...</div>
        ) : (
          <>
            {/* ── RACCOURCIS ── */}
            <div className={styles.shortcuts}>
              <button onClick={() => navigate('/inventaire')} className={styles.shortcut}>
                <span className={styles.shortcutIcon}>🔢</span>
                <span>Faire l'inventaire</span>
              </button>
              <button
                onClick={() => navigate('/produits')}
                className={`${styles.shortcut} ${statsStock.ruptures.length > 0 ? styles.shortcutAlert : ''}`}
              >
                <span className={styles.shortcutIcon}>🚨</span>
                <span>
                  {statsStock.ruptures.length > 0
                    ? `${statsStock.ruptures.length} rupture${statsStock.ruptures.length > 1 ? 's' : ''}`
                    : 'Ruptures de stock'}
                </span>
              </button>
              <button onClick={() => navigate('/commandes')} className={styles.shortcut}>
                <span className={styles.shortcutIcon}>🛒</span>
                <span>Commandes</span>
              </button>
              <button onClick={() => navigate('/produits')} className={styles.shortcut}>
                <span className={styles.shortcutIcon}>📦</span>
                <span>Produits</span>
              </button>
            </div>

            {/* ── STATS COMMANDES ── */}
            <div className={styles.sectionLabel}>Commandes</div>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statIcon}>🛒</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsCommandes.nbTotal}</div>
                  <div className={styles.statLabel}>Commandes totales</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.cardGreen}`}>
                <div className={styles.statIcon}>💶</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{fmt(statsCommandes.caTotal)}</div>
                  <div className={styles.statLabel}>CA total</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.cardBlue}`}>
                <div className={styles.statIcon}>📅</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{fmt(statsCommandes.caAujourdhui)}</div>
                  <div className={styles.statLabel}>CA aujourd'hui</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.cardTeal}`}>
                <div className={styles.statIcon}>🗓️</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{fmt(statsCommandes.caMois)}</div>
                  <div className={styles.statLabel}>CA {moisLabel}</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.cardOrange}`}>
                <div className={styles.statIcon}>⏳</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsCommandes.enAttentePaiement}</div>
                  <div className={styles.statLabel}>En attente paiement</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.cardPurple}`}>
                <div className={styles.statIcon}>📦</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsCommandes.enPreparation}</div>
                  <div className={styles.statLabel}>En préparation</div>
                </div>
              </div>
            </div>

            {/* ── STATS STOCK ── */}
            <div className={styles.sectionLabel}>Stock</div>
            <div className={styles.statsGrid}>
              <div className={`${styles.statCard} ${statsStock.ruptures.length > 0 ? styles.cardDanger : styles.cardGreen}`}>
                <div className={styles.statIcon}>{statsStock.ruptures.length > 0 ? '🚨' : '✅'}</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsStock.ruptures.length}</div>
                  <div className={styles.statLabel}>Ruptures de stock</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${statsStock.critiques.length > 0 ? styles.cardOrange : styles.cardGreen}`}>
                <div className={styles.statIcon}>⚠️</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsStock.critiques.length}</div>
                  <div className={styles.statLabel}>Stock critique (≤ {SEUIL_CRITIQUE})</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.cardGreen}`}>
                <div className={styles.statIcon}>💰</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{fmt(statsStock.valeur)}</div>
                  <div className={styles.statLabel}>Valeur du stock</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${statsStock.tauxDispo >= 80 ? styles.cardGreen : styles.cardOrange}`}>
                <div className={styles.statIcon}>📊</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsStock.tauxDispo}%</div>
                  <div className={styles.statLabel}>Taux de disponibilité</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.cardBlue}`}>
                <div className={styles.statIcon}>📦</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsStock.total}</div>
                  <div className={styles.statLabel}>Produits au total</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${statsStock.masques > 0 ? styles.cardPurple : ''}`}>
                <div className={styles.statIcon}>👁️</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{statsStock.masques}</div>
                  <div className={styles.statLabel}>Produits masqués</div>
                </div>
              </div>
            </div>

            {/* ── DEUX COLONNES : RUPTURES + BLOQUÉES ── */}
            <div className={styles.twoCol}>

              {/* Ruptures */}
              <div className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>
                    🚨 Ruptures de stock
                    {statsStock.ruptures.length > 0 && (
                      <span className={`${styles.badge} ${styles.badgeRed}`} style={{ marginLeft: '0.5rem' }}>
                        {statsStock.ruptures.length}
                      </span>
                    )}
                  </h2>
                  <button onClick={() => navigate('/produits')} className={styles.btnSeeAll}>Gérer →</button>
                </div>
                {statsStock.ruptures.length === 0 ? (
                  <div className={styles.emptyState}>✅ Aucune rupture de stock</div>
                ) : (
                  <ul className={styles.alertList}>
                    {statsStock.ruptures.slice(0, 8).map((p) => (
                      <li
                        key={p.id}
                        className={styles.alertItem}
                        onClick={() => navigate(`/produits/${p.id}`)}
                      >
                        <span className={styles.alertDot} style={{ background: '#e53e3e' }} />
                        <span className={styles.alertName}>{p.name}</span>
                        <span className={styles.alertBadge} style={{ background: '#fed7d7', color: '#9b2c2c' }}>
                          Stock 0
                        </span>
                      </li>
                    ))}
                    {statsStock.ruptures.length > 8 && (
                      <li className={styles.alertMore}>
                        +{statsStock.ruptures.length - 8} de plus
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {/* Stock critique */}
              <div className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>
                    ⚠️ Stock critique
                    {statsStock.critiques.length > 0 && (
                      <span className={`${styles.badge} ${styles.badgeOrange}`} style={{ marginLeft: '0.5rem' }}>
                        {statsStock.critiques.length}
                      </span>
                    )}
                  </h2>
                  <button onClick={() => navigate('/inventaire')} className={styles.btnSeeAll}>Inventaire →</button>
                </div>
                {statsStock.critiques.length === 0 ? (
                  <div className={styles.emptyState}>✅ Tous les stocks sont OK</div>
                ) : (
                  <ul className={styles.alertList}>
                    {statsStock.critiques.slice(0, 8).map((p) => (
                      <li
                        key={p.id}
                        className={styles.alertItem}
                        onClick={() => navigate(`/produits/${p.id}`)}
                      >
                        <span className={styles.alertDot} style={{ background: '#ed8936' }} />
                        <span className={styles.alertName}>{p.name}</span>
                        <span className={styles.alertBadge} style={{ background: '#feebc8', color: '#9c4221' }}>
                          Stock {p.stock}
                        </span>
                      </li>
                    ))}
                    {statsStock.critiques.length > 8 && (
                      <li className={styles.alertMore}>
                        +{statsStock.critiques.length - 8} de plus
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>

            {/* ── COMMANDES BLOQUÉES ── */}
            {commandesBloquees.length > 0 && (
              <div className={styles.panel} style={{ marginBottom: '1.5rem' }}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>
                    🔴 Commandes bloquées en préparation
                    <span className={`${styles.badge} ${styles.badgeRed}`} style={{ marginLeft: '0.5rem' }}>
                      {commandesBloquees.length}
                    </span>
                  </h2>
                  <button onClick={() => navigate('/commandes')} className={styles.btnSeeAll}>Voir tout →</button>
                </div>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Client</th>
                        <th>Date commande</th>
                        <th>Total</th>
                        <th>Paiement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commandesBloquees.map((c) => (
                        <tr key={c.id} className={styles.row} onClick={() => navigate(`/commandes/${c.id}`)}>
                          <td className={styles.id}>#{c.id}</td>
                          <td>{c.prenomClient} {c.nomClient}</td>
                          <td>
                            <span className={styles.dateAlert}>{fmtDate(c.dateCommande)}</span>
                          </td>
                          <td className={styles.price}>{fmt(c.total ?? 0)}</td>
                          <td>{getBadgePaiement(c.statutPaiement)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── COMMANDES RÉCENTES ── */}
            {recentCommandes.length > 0 && (
              <div className={styles.panel}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>Commandes récentes</h2>
                  <button onClick={() => navigate('/commandes')} className={styles.btnSeeAll}>Voir tout →</button>
                </div>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Client</th>
                        <th>Date</th>
                        <th>Total</th>
                        <th>Paiement</th>
                        <th>Livraison</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentCommandes.map((c) => (
                        <tr key={c.id} className={styles.row} onClick={() => navigate(`/commandes/${c.id}`)}>
                          <td className={styles.id}>#{c.id}</td>
                          <td>{c.prenomClient} {c.nomClient}</td>
                          <td>{fmtDate(c.dateCommande)}</td>
                          <td className={styles.price}>{fmt(c.total ?? 0)}</td>
                          <td>{getBadgePaiement(c.statutPaiement)}</td>
                          <td>
                            <span className={styles.statutLivraison}>
                              {c.statutLivraison?.replace(/_/g, ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};
